import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getTeamDetailsApi,
  getTeamTasksApi,
} from "../../../../app/api";
import CreateTeamTaskDrawer from "./CreateTeamTaskDrawer";

export default function TeamDetailsPage() {
  const { id } = useParams();

  const [team, setTeam] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [showDrawer, setShowDrawer] = useState(false);
  const [activeTab, setActiveTab] = useState("ALL");

  useEffect(() => {
    loadTeam();
    loadTasks();
  }, [id]);

  const loadTeam = async () => {
    const res = await getTeamDetailsApi(id);
    if (res?.success) {
      setTeam(res.data);
    }
  };

  const loadTasks = async () => {
    const res = await getTeamTasksApi(id);
    if (res?.success) {
      setTasks(res.data || []);
    }
  };

  const filteredTasks =
    activeTab === "ALL"
      ? tasks
      : tasks.filter((t) => t.status === activeTab);

  if (!team) return <div className="p-8 text-white">Loading...</div>;

  return (
    <div
      className="
        relative
        w-full
        bg-gradient-to-br
        from-[#1e1447]
        via-[#24155a]
        to-[#12082f]
        p-6 rounded-[24px] md:mt-20
        overflow-hidden
      "
    >
      {/* Floating Lights */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-white/20 rounded-full blur-3xl opacity-20 pointer-events-none"></div>
      <div className="absolute bottom-10 right-10 w-72 h-72 bg-purple-400/20 rounded-full blur-3xl opacity-20 pointer-events-none"></div>

      {/* ===== HEADER ===== */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white">
            {team.name}
          </h1>
          <p className="text-gray-300 mt-1">
            Department: {team.department_name}
          </p>
        </div>

        <button
          onClick={() => setShowDrawer(true)}
          className="
            px-5 py-2 rounded-[12px]
            bg-white/10 backdrop-blur-md
            border border-white/20
            text-white
            hover:bg-white/20
            transition
          "
        >
          + Create Team Task
        </button>
      </div>

      {/* ===== FILTER TABS ===== */}
      <div className="flex gap-3 mb-6">
        {["ALL", "TODO", "IN_PROGRESS", "DONE"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`
              px-4 py-1 text-sm rounded-[10px]
              backdrop-blur-md border border-white/10
              transition-all duration-300
              ${
                activeTab === tab
                  ? "bg-white/20 text-white"
                  : "bg-white/10 text-gray-300 hover:bg-white/15"
              }
            `}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ===== TASK CARDS ===== */}
      {filteredTasks.length === 0 ? (
        <p className="text-gray-300">
          No tasks assigned to this team.
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {filteredTasks.map((task) => (
            <div
              key={task.id}
              className="
                relative
                overflow-hidden
                rounded-[20px]
                p-5
                backdrop-blur-2xl
                bg-white/5
                border border-white/20
                shadow-[0_10px_40px_rgba(0,0,0,0.35)]
                hover:bg-white/10
                transition-all duration-300
              "
            >
              {/* Shine Effect */}
              <div className="absolute -top-16 -right-16 w-40 h-40 bg-white/50 rounded-full blur-2xl opacity-40 pointer-events-none"></div>

              <h4 className="text-white font-semibold text-lg">
                {task.title}
              </h4>

              <p className="text-gray-400 text-sm mt-2 line-clamp-2">
                {task.description}
              </p>

              {/* Dates */}
              <div className="flex justify-between text-xs text-gray-400 mt-4">
                <div>
                  <p>Start</p>
                  <p className="text-gray-300">
                    {task.created_at
                      ? task.created_at.split(" ")[0]
                      : "--"}
                  </p>
                </div>

                <div>
                  <p>End</p>
                  <p className="text-gray-300">
                    {task.end_date || "--"}
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              <div
                className="h-1 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full mt-3"
                style={{
                  width:
                    task.status === "DONE"
                      ? "100%"
                      : task.progress
                      ? `${task.progress}%`
                      : "40%",
                }}
              />

              {/* Status Badge */}
              <div className="flex justify-end mt-2">
                <span
                  className={`
                    text-[8px] px-3 py-0.5 rounded-full font-medium
                    backdrop-blur-md border border-white/20
                    ${
                      task.status === "DONE"
                        ? "bg-green-500/20 text-green-400"
                        : task.status === "IN_PROGRESS"
                        ? "bg-cyan-500/20 text-cyan-400"
                        : "bg-orange-500/20 text-orange-400"
                    }
                  `}
                >
                  {task.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ===== DRAWER ===== */}
      <CreateTeamTaskDrawer
        team={team}
        open={showDrawer}
        onClose={() => setShowDrawer(false)}
        onCreated={loadTasks}
      />
    </div>
  );
}